import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  AppBar,
  Badge,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
  alpha,
  Stack,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  FolderOpen as OrganizerIcon,
  // AccountCircle as AccountIcon, // Temporarily unused - Profile menu item hidden
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  SwapHoriz as SwapIcon,
  Groups as GroupsIcon,
  AccountBalance as TrustIcon,
  HomeWork as EstateIcon,
  Notifications as NotificationsIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Close as CloseIcon,
  Assessment as AuditIcon,
} from '@mui/icons-material';
import { ClientType, ServiceLine } from '@ata-digital/client-tax-system-api-client';
import { useLogout, useAuthState } from '../../hooks/auth/useAuth';
import { useClient } from '@/hooks/context/useClient';
import { useUnreadMessageCount } from '@/hooks/api/useUnreadMessageCount';
import { AppLogo } from '../branding/AppLogo';
import { ImpersonationBanner } from './ImpersonationBanner';
import { createLogger } from '@/utils/logger';
import { maskTaxIdForDisplay } from '@/utils/taxIdValidation';
import { HEADER_HEIGHT } from '@/constants/layout';

const log = createLogger('MainLayout');

interface MainLayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 260;

interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  isMessages?: boolean;
  /** Optional search params applied when navigating (used for the Tax/Audit binder views). */
  search?: { line: 'tax' | 'audit' };
}

export function MainLayout({ children }: MainLayoutProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [clientMenuAnchorEl, setClientMenuAnchorEl] = useState<null | HTMLElement>(null);

  const { selectedClient, availableClients, setSelectedClient } = useClient();
  const logoutMutation = useLogout();
  const { data: authState } = useAuthState();
  const { totalUnread, firstUnreadBinderId } = useUnreadMessageCount();

  // Binder nav destinations are gated on the active client's enabled service lines.
  const hasTaxServiceLine = selectedClient?.serviceLines?.includes(ServiceLine.Tax) ?? false;
  const hasAuditServiceLine = selectedClient?.serviceLines?.includes(ServiceLine.Audit) ?? false;
  const navigationItems: NavItem[] = [
    { text: 'Home', icon: <HomeIcon />, path: '/' },
    ...(hasTaxServiceLine
      ? [{ text: 'Tax Binders', icon: <OrganizerIcon />, path: '/binders', search: { line: 'tax' as const } }]
      : []),
    ...(hasAuditServiceLine
      ? [{ text: 'Audit Binders', icon: <AuditIcon />, path: '/binders', search: { line: 'audit' as const } }]
      : []),
    { text: 'Messages', icon: <ChatBubbleOutlineIcon />, path: '/binders', isMessages: true },
    { text: 'Notifications', icon: <NotificationsIcon />, path: '/notification-settings' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/user-settings' },
  ];

  // Get user data from auth state
  const currentUser = authState?.user
    ? {
        name:
          authState.user.fullName ||
          `${authState.user.firstName || ''} ${authState.user.lastName || ''}`.trim() ||
          'User',
        email: authState.user.email || '',
        firstName: authState.user.firstName,
        lastName: authState.user.lastName,
      }
    : null;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
      setAnchorEl(null);
      setSelectedClient(null);
      // The useLogout hook already clears localStorage and query cache
      // Using window.location.replace() intentionally (not React Router navigate)
      // for security: forces full page reload to clear all in-memory state
      window.location.replace('/login');
    } catch (error) {
      log.error('Logout failed:', error);
      // Force logout even if API call fails - security requires complete state reset
      setAnchorEl(null);
      setSelectedClient(null);
      window.location.replace('/login');
    }
  };

  const handleClientMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setClientMenuAnchorEl(event.currentTarget);
  };

  const handleClientMenuClose = () => {
    setClientMenuAnchorEl(null);
  };

  const handleClientSwitch = async (clientId: string) => {
    const client = availableClients.find((c) => c.id === clientId);
    if (client) {
      // Cancel any in-flight binder queries first
      await queryClient.cancelQueries({ queryKey: ['taxBinders'] });
      // Remove binders cache completely to ensure fresh data for the new client
      queryClient.removeQueries({ queryKey: ['taxBinders'] });
      // Set the new client
      setSelectedClient(client);
      // Navigate to home page - cache removal forces fresh fetch
      navigate({ to: '/' });
    }
    handleClientMenuClose();
  };

  const handleChangeClient = () => {
    // Navigate to client selection page
    navigate({ to: '/client-select' });
    handleClientMenuClose();
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!currentUser) return 'U';

    // Use firstName and lastName if available
    if (currentUser.firstName || currentUser.lastName) {
      return `${currentUser.firstName?.[0] || ''}${currentUser.lastName?.[0] || ''}`.toUpperCase();
    }

    // Fall back to name parsing
    if (currentUser.name) {
      const names = currentUser.name.split(' ');
      return names
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }

    return 'U';
  };

  /**
   * Maps ClientType enum from API to display icon
   * Handles all ClientType values including Corporation, EmployeePlan, Fiduciary, etc.
   */
  const getClientIcon = (type?: ClientType) => {
    switch (type) {
      case ClientType.Individual:
        return <PersonIcon fontSize="small" />;
      case ClientType.Corporation:
      case ClientType.SoleProprietorship:
      case ClientType.NonProfit:
        return <BusinessIcon fontSize="small" />;
      case ClientType.Partnership:
        return <GroupsIcon fontSize="small" />;
      case ClientType.Fiduciary:
        return <TrustIcon fontSize="small" />;
      case ClientType.EmployeePlan:
        return <EstateIcon fontSize="small" />;
      case ClientType.Other:
      case ClientType.Staff:
      case ClientType.Project:
      default:
        return <BusinessIcon fontSize="small" />;
    }
  };

  // If no client is selected, don't show the navigation
  if (!selectedClient) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.grey[50] }}>
        <CssBaseline />
        {children}
      </Box>
    );
  }

  const drawer = (
    <Box>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.dark} 100%)`,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: { xs: HEADER_HEIGHT.xs, sm: HEADER_HEIGHT.sm },
          boxSizing: 'content-box',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          px: 2,
        }}
      >
        <AppLogo variant="nav" height={36} width="auto" />
        <IconButton
          onClick={handleDrawerToggle}
          aria-label="close drawer"
          sx={{
            color: 'white',
            display: { xs: 'flex', md: 'none' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {navigationItems.map((item) => {
          // Some items navigate programmatically (with search params or a deep link). For those
          // we preventDefault so the Link's own param-less navigation to `to` doesn't override us.
          const handleClick = (event: React.MouseEvent) => {
            if (isMobile) setMobileOpen(false);
            if (item.isMessages && firstUnreadBinderId) {
              event.preventDefault();
              void navigate({
                to: '/tax-binders/$binderId',
                params: { binderId: firstUnreadBinderId },
                search: { tab: 'messages' },
              });
              return;
            }
            // Tax/Audit binder views: apply the ?line= filter (Tax vs Audit)
            if (item.search) {
              event.preventDefault();
              void navigate({ to: '/binders', search: item.search });
              return;
            }
          };

          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton component={Link} to={item.path} onClick={handleClick}>
                <ListItemIcon>
                  {item.isMessages && totalUnread > 0 ? (
                    <Badge
                      badgeContent={totalUnread}
                      color="error"
                      max={99}
                      aria-label={`${totalUnread} unread messages`}
                    >
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      {/* TODO: We need to work on creating these features before re-enabling the menu items, as this confuses users. */}
      {/* <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton>
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </ListItemButton>
        </ListItem>
      </List> */}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          ml: { xs: 0, md: `${drawerWidth}px` },
          background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
          borderRadius: 0,
        }}
      >
        <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: { xs: 1, sm: 2 }, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          {/* Logo container - logo slides left and shrinks as chip needs more space (mobile only) */}
          <Box
            sx={{
              flexGrow: 1,
              flexShrink: 1,
              minWidth: 0,
              display: { xs: 'flex', md: 'none' }, // Hide entire container on desktop, not just the logo
              alignItems: 'center',
              justifyContent: 'flex-end', // Logo aligns to right within its container, which shrinks as chip needs more space
            }}
          >
            <AppLogo
              variant="header-white"
              height={28}
              sx={{
                flexShrink: 1, // Allow logo to shrink
                minWidth: 0,
              }}
            />
          </Box>

          {/* Spacer - pushes navigation items to the right */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Client Switcher - Only show for client users with multiple clients */}
          {authState?.userType === 'client' && availableClients.length > 1 && (
            <Box
              sx={{
                mr: { xs: 1, sm: 2 },
                flexShrink: 0,
                maxWidth: 'calc(100vw - 120px)', // Reserve space for hamburger + avatar + margins
                overflow: 'hidden',
              }}
            >
              <Chip
                icon={getClientIcon(selectedClient.type)}
                label={selectedClient.name}
                onClick={handleClientMenuOpen}
                onDelete={handleClientMenuOpen}
                deleteIcon={<SwapIcon />}
                sx={{
                  bgcolor: alpha(theme.palette.common.white, 0.15),
                  color: 'white',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                  fontWeight: 500,
                  maxWidth: '100%',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.25),
                    transform: 'scale(1.02)',
                  },
                  '& .MuiChip-icon': {
                    color: alpha(theme.palette.common.white, 0.9),
                  },
                  '& .MuiChip-deleteIcon': {
                    color: alpha(theme.palette.common.white, 0.8),
                    '&:hover': {
                      color: 'white',
                    },
                  },
                }}
              />
              <Menu
                anchorEl={clientMenuAnchorEl}
                open={Boolean(clientMenuAnchorEl)}
                onClose={handleClientMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem disabled>
                  <Typography variant="caption" color="text.secondary">
                    Switch Client
                  </Typography>
                </MenuItem>
                <Divider />
                {availableClients.map((client) => (
                  <MenuItem
                    key={client.id}
                    onClick={() => handleClientSwitch(client.id || '')}
                    selected={client.id === selectedClient.id}
                  >
                    <ListItemIcon>{getClientIcon(client.type)}</ListItemIcon>
                    <ListItemText
                      primary={client.name}
                      secondary={maskTaxIdForDisplay(
                        client.maskedTaxId,
                        client.type === ClientType.Individual ? 'SSN' : 'EIN'
                      )}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary',
                      }}
                    />
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem onClick={handleChangeClient}>
                  <ListItemIcon>
                    <SwapIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Change Client</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          )}

          {/* User Menu */}
          <Tooltip title={currentUser?.name || 'User'}>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: alpha(theme.palette.common.white, 0.15),
                  backdropFilter: 'blur(10px)',
                  border: `2px solid ${alpha(theme.palette.common.white, 0.2)}`,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              >
                {getUserInitials()}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            {currentUser && [
              <MenuItem key="user-info" disabled>
                <Stack>
                  <Typography variant="body2" color="text.secondary">
                    {currentUser.name}
                  </Typography>
                  {currentUser.email && (
                    <Typography variant="caption" color="text.secondary">
                      {currentUser.email}
                    </Typography>
                  )}
                </Stack>
              </MenuItem>,
              <Divider key="divider-1" />,
            ]}
            <MenuItem
              onClick={() => {
                handleProfileMenuClose();
                navigate({ to: '/user-settings' });
              }}
            >
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Settings</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }} aria-label="navigation drawer">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRadius: 0,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRadius: 0,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.light, 0.02)} 100%)`,
          minHeight: '100vh',
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          boxSizing: 'border-box',
        }}
      >
        <Toolbar />
        {/* Impersonation Banner - shows when preparer is viewing as client */}
        <ImpersonationBanner />
        <Box
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
